import React, { useCallback, useEffect, useState } from 'react';
import { CloseOutlined } from '@ant-design/icons';
import { Button } from 'antd';

import ProductUserApi from '@Modules/track/users/UsersApi';
import { isReachabilityReportAllZeros } from '@Utils/isReachabilityReportAllZeros';
import useSdkSetupHandoff from '@Utils/useSdkSetupHandoff';
import {
  isSdkSetupSidebarCardDismissed,
  persistSdkSetupSidebarCardDismissed,
} from '@Utils/sdkSetupSidebarCardStorage';

import style from './SdkSetupSidebarCard.module.less';

export default function SdkSetupSidebarCard({ productId, currentUser }) {
  const [showCard, setShowCard] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    isSdkSetupSidebarCardDismissed(productId)
  );

  const handoffEnabled = showCard === true && !dismissed;
  const { loading, sdkReady, onSetupNow } = useSdkSetupHandoff({
    productId,
    currentUser,
    enabled: handoffEnabled,
  });

  useEffect(() => {
    setDismissed(isSdkSetupSidebarCardDismissed(productId));
  }, [productId]);

  useEffect(() => {
    if (!productId) {
      setShowCard(null);
      return undefined;
    }

    let cancelled = false;
    setShowCard(null);

    ProductUserApi.reachabilityReport()
      .then((report) => {
        if (cancelled) {
          return;
        }
        setShowCard(isReachabilityReportAllZeros(report));
      })
      .catch(() => {
        if (!cancelled) {
          setShowCard(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [productId]);

  const onDismiss = useCallback(() => {
    persistSdkSetupSidebarCardDismissed(productId);
    setDismissed(true);
  }, [productId]);

  if (!handoffEnabled) {
    return null;
  }

  return (
    <div className={style.root}>
      <div className={style.card}>
        <div className={style.headerRow}>
          <h3 className={style.title}>SDK Setup</h3>
          <Button
            type="text"
            size="small"
            className={style.closeBtn}
            icon={<CloseOutlined />}
            aria-label="Close SDK setup card"
            onClick={onDismiss}
          />
        </div>
        <span className={style.badge}>just in 29 minutes</span>
        <p className={style.description}>
          If you close this, setup is still available on the SDK page whenever
          you need it.
        </p>
        <Button
          type="primary"
          block
          className={style.cta}
          loading={loading}
          disabled={loading || !sdkReady}
          onClick={onSetupNow}
        >
          Setup now
        </Button>
      </div>
    </div>
  );
}
